<template>
  <div class="inventory-table-container">
    <!-- ドラッグモード切り替えトグル -->
    <div class="drag-mode-toggle">
      <label class="toggle-switch">
        <span class="toggle-label">スクロール</span>
        <div class="toggle-container">
          <input 
            type="checkbox" 
            :checked="isDragModeEnabled" 
            @change="toggleDragMode"
          />
          <span class="toggle-slider"></span>
        </div>
      </label>
    </div>
    
    <!-- テーブル -->
    <div class="table-container">
      <div 
        class="table-scroll-container" 
        ref="tableContainer"
        @mousedown="handleMouseDown"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
        :style="{ userSelect: isDragModeEnabled ? 'none' : 'text' }"
      >
        <table class="table table-bordered">
          <thead class="table-light">
            <tr>
              <th 
                v-for="column in columns" 
                :key="column.key" 
                :style="[getColumnStyle(column), { backgroundColor: '#195983' }]"
                v-html="column.label"
              >
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in items" :key="index">
              <td :style="[getColumnStyle(columns[0]), { textAlign: 'center' }]">{{ item.categoryNo }}</td>
              <td :style="[getColumnStyle(columns[1]), { textAlign: 'left' }]">{{ item.categoryName }}</td>
              <td 
                v-for="column in monthColumns" 
                :key="column.key" 
                :style="[getColumnStyle(column), { backgroundColor: isLastColumn(column.key) ? '#ffed27' : 'white', textAlign: 'right' }]"
              >
                {{ formatNumber(item.monthlyData[column.key]) }}
              </td>
            </tr>
            <tr v-if="summaryData" class="summary-row">
              <td colspan="2" :style="[getColumnStyle(columns[0]), getColumnStyle(columns[1]), getSummaryRowStyle('total', ''), { textAlign: 'center' }]"><strong>統計</strong></td>
              <td 
                v-for="column in monthColumns" 
                :key="column.key" 
                :style="[getColumnStyle(column), getSummaryRowStyle('total', column.key), { textAlign: 'right' }]"
              >
                <strong>{{ formatNumber(summaryData.monthlyData[column.key]) }}</strong>
              </td>
            </tr>
            <tr v-if="summaryData" class="summary-row">
              <td colspan="2" :style="[getColumnStyle(columns[0]), getColumnStyle(columns[1]), getSummaryRowStyle('changeFromPrevious', ''), { textAlign: 'center' }]"><strong>前回より増減</strong></td>
              <td 
                v-for="column in monthColumns" 
                :key="column.key" 
                :style="[getColumnStyle(column), getSummaryRowStyle('changeFromPrevious', column.key), { textAlign: 'right' }]"
              >
                <strong>{{ formatNumber(summaryData.changeFromPrevious[column.key]) }}</strong>
              </td>
            </tr>
            <tr v-if="summaryData" class="summary-row">
              <td colspan="2" :style="[getColumnStyle(columns[0]), getColumnStyle(columns[1]), getSummaryRowStyle('periodGrossMarginRate', ''), { textAlign: 'center' }]"><strong>期間原価率</strong></td>
              <td 
                v-for="column in monthColumns" 
                :key="column.key" 
                :style="[getColumnStyle(column), getSummaryRowStyle('periodGrossMarginRate', column.key), { textAlign: 'right' }]"
              >
                <strong>{{ formatPercentage(summaryData.periodGrossMarginRate[column.key]) }}%</strong>
              </td>
            </tr>
            <tr v-if="summaryData" class="summary-row">
              <td colspan="2" :style="[getColumnStyle(columns[0]), getColumnStyle(columns[1]), getSummaryRowStyle('periodGrossMarginRateTotal', ''), { textAlign: 'center' }]"><strong>期間原価率トータル</strong></td>
              <td 
                v-for="column in monthColumns" 
                :key="column.key" 
                :style="[getColumnStyle(column), getSummaryRowStyle('periodGrossMarginRateTotal', column.key), { textAlign: 'right' }]"
              >
                <strong>{{ formatPercentage(summaryData.periodGrossMarginRateTotal[column.key]) }}%</strong>
              </td>
            </tr>
            <tr v-if="summaryData" class="summary-row">
              <td colspan="2" :style="[getColumnStyle(columns[0]), getColumnStyle(columns[1]), getSummaryRowStyle('inventoryTurnoverRate', ''), { textAlign: 'center' }]"><strong>在庫回転率</strong></td>
              <td 
                v-for="column in monthColumns" 
                :key="column.key" 
                :style="[getColumnStyle(column), getSummaryRowStyle('inventoryTurnoverRate', column.key), { textAlign: 'right' }]"
              >
                <strong>{{ formatPercentage(summaryData.inventoryTurnoverRate[column.key]) }}%</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InventoryImplementationTable',
  props: {
    items: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    summaryData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      isDragModeEnabled: true
    }
  },
  computed: {
    monthColumns() {
      return this.columns.slice(2);
    }
  },
  methods: {
    formatNumber(value) {
      if (value === null || value === undefined) return '0';
      return value.toLocaleString();
    },
    
    /**
     * パーセンテージ値を小数第一位までフォーマット
     * @param {number} value - パーセンテージ値
     * @returns {string} フォーマット済みの文字列
     */
    formatPercentage(value) {
      if (value === null || value === undefined) return '0.0';
      return Number(value).toFixed(1);
    },
    getSummaryRowStyle(rowType, monthKey) {
      const baseStyle = {};
      // 最後の列は常に黄色
      if (this.isLastColumn(monthKey)) {
        baseStyle.backgroundColor = '#ffed27';
        return baseStyle;
      }
      // 行タイプに応じて背景色を設定
      if (rowType === 'total' || rowType === 'periodGrossMarginRate' || rowType === 'inventoryTurnoverRate') {
        baseStyle.backgroundColor = '#d3d3d3';
      } else if (rowType === 'changeFromPrevious' || rowType === 'periodGrossMarginRateTotal') {
        baseStyle.backgroundColor = 'white';
      }
      return baseStyle;
    },
    
    isLastColumn(columnKey) {
      if (!this.monthColumns || this.monthColumns.length === 0) return false;
      const lastColumn = this.monthColumns[this.monthColumns.length - 1];
      return columnKey === lastColumn.key;
    },
    
    getColumnStyle(column) {
      const style = {};
      
      if (column.minWidth) {
        style.minWidth = `${column.minWidth}px`;
      }
      
      if (column.maxWidth) {
        style.maxWidth = `${column.maxWidth}px`;
        style.overflow = 'hidden';
        style.whiteSpace = 'normal';
        style.wordWrap = 'break-word';
      }
      
      return style;
    },
    
    getColumnStyleForMonthKey(monthKey) {
      const column = this.columns.find(col => col.key === monthKey);
      return column ? this.getColumnStyle(column) : {};
    },
    
    toggleDragMode() {
      this.isDragModeEnabled = !this.isDragModeEnabled;
      if (this.$refs.tableContainer) {
        this.$refs.tableContainer.style.cursor = this.isDragModeEnabled ? 'default' : 'auto';
      }
    },
    
    handleMouseDown(e) {
      if (!this.isDragModeEnabled) {
        return;
      }
      this.startDrag(e);
    },
    
    startDrag(e) {
      if (!this.isDragModeEnabled) return;
      
      this.isDragging = true;
      this.startX = e.pageX - this.$refs.tableContainer.offsetLeft;
      this.scrollLeft = this.$refs.tableContainer.scrollLeft;
      this.$refs.tableContainer.style.cursor = 'grabbing';
      e.preventDefault();
    },
    
    onDrag(e) {
      if (!this.isDragging || !this.isDragModeEnabled) return;
      const x = e.pageX - this.$refs.tableContainer.offsetLeft;
      const walk = (x - this.startX) * 2;
      this.$refs.tableContainer.scrollLeft = this.scrollLeft - walk;
    },
    
    stopDrag() {
      this.isDragging = false;
      if (this.$refs.tableContainer) {
        this.$refs.tableContainer.style.cursor = 'default';
      }
    }
  }
}
</script>

<style scoped>
.inventory-table-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

/* ドラッグモード切り替えトグル */
.drag-mode-toggle {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 10px;
}

/* トグルスイッチのスタイル */
.toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.toggle-label {
  margin-right: 8px;
  font-size: 14px;
}

.toggle-container {
  position: relative;
  width: 40px;
  height: 20px;
}

.toggle-container input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 34px;
  transition: .4s;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  border-radius: 50%;
  transition: .4s;
}

input:checked + .toggle-slider {
  background-color: #4CD964;
}

input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

/* テーブルコンテナ */
.table-container {
  position: relative;
  margin-bottom: 1rem;
  overflow: visible;
}

/* スクロール可能なテーブル部分 */
.table-scroll-container {
  overflow-x: auto;
  cursor: default;
  max-width: 100%;
  position: relative;
}

.table th {
  padding: 0.75rem;
  text-align: center;
  vertical-align: middle;
  color: white;
  border: 1px solid #dee2e6;
}

.table td {
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  white-space: nowrap;
}

.summary-row {
  font-weight: bold;
  background-color: #f8f9fa;
}

.align-middle {
  vertical-align: middle !important;
}
</style>
